import api from '@/api/api';
import showConfirm from '@/components/utils/ui_v4/alerts/confirm/confirm';
import { toast } from '@/components/utils/ui_v4/alerts/toast/toast';
import { API_ENDPOINTS, ERROR_MESSAGES, SUCCESS_MESSAGES, getStatusColor, getStatusIcon, getStatusName } from '@/components/webcotacao/constants';
import router from '@/router';
import { computed, ref } from 'vue';
import useCotacaoStore from './useCotacaoStore';
import useNovaCotacaoForm from './useNovaCotacaoForm';
import useNovaCotacaoPayload from './useNovaCotacaoPayload';
import useNovaCotacaoValidation from './useNovaCotacaoValidation';

export default function useNovaCotacaoDialog(props) {
  const isDeleting = ref(false);
  const confirmDeleteDialog = ref(false);

  let salvarRascunho;

  // Composables especializados - passar salvarRascunho como callback
  const formComposable = useNovaCotacaoForm(props, () => salvarRascunho());
  const store = useCotacaoStore();

  // Preparar dados para composables filhos
  const validationComposable = useNovaCotacaoValidation(
    formComposable.dadosGerais,
    formComposable // formComposable tem { produtos, fornecedores, ... } que o validation precisa no 2º arg
  );

  const payloadComposable = useNovaCotacaoPayload(formComposable);

  const hasFornecedoresSemContato = computed(() => {
    return validationComposable.validateFornecedoresRepresentantes().hasWarning;
  });

  const close = async () => {
    if (formComposable.cotacao.value && formComposable.cotacao.value.publicar_cotacao) {
      props.handleClose();
      if (router.currentRoute.path !== '/cotacoes/listar-cotacoes') {
        await router.push('/cotacoes/listar-cotacoes');
      }
      return;
    }
    props.handleClose();
  };

  const closeConfirmDeleteDialog = () => {
    confirmDeleteDialog.value = false;
  };

  salvarRascunho = async () => {
    if (!props.idCotacao) {
      toast.error('ID da cotação não informado.');
      return false;
    }

    if (formComposable.dadosGerais.titulo) {
      const tituloTrimmed = formComposable.dadosGerais.titulo.trim();
      if (tituloTrimmed === '') {
        formComposable.dadosGerais.titulo = '';
        validationComposable.validate.titulo = 'O título não pode conter apenas espaços em branco';
        toast.error('O título não pode conter apenas espaços em branco');
        return false;
      }
      formComposable.dadosGerais.titulo = tituloTrimmed;
    }

    // Validar formulário
    const validation = validationComposable.validateRascunho();
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return false;
    }

    // Verificar fornecedores sem representantes
    const { hasWarning, fornecedoresSemRepresentantes } =
      validationComposable.validateFornecedoresRepresentantes();

    if (hasWarning) {
      const nomesFornecedores = fornecedoresSemRepresentantes
        .map((f, index) => `${index + 1}. ${f.razao_social || f.nome_fantasia || `Fornecedor ID ${f.id || f.idfornecedor}`}`)
        .join('<br>');
      toast.warning(
        `Atenção: ${fornecedoresSemRepresentantes.length} fornecedor(es) sem representante(s) selecionado(s) para a cotação:<br>${nomesFornecedores}<br><br>O rascunho será salvo, mas esses fornecedores não receberão a cotação.`
      );
    }

    // Montar payload
    const payload = payloadComposable.montarPayload();
    if (!payload.config) {
      toast.error(ERROR_MESSAGES.CONFIG_REQUIRED);
      return false;
    }

    formComposable.isSavingRascunho.value = true;
    try {
      await api.put(
        `${API_ENDPOINTS.ATUALIZAR_COTACAO}/${props.idCotacao}`,
        payload
      );
      toast.success(SUCCESS_MESSAGES.SAVE_RASCUNHO);

      // Recarregar cotação
      await formComposable.getCotacao();

      // Atualizar store
      await store.refreshCotacao(props.idCotacao);
      await store.refresh();

      return true;
    } catch (error) {
      const message =
        (error &&
          error.response &&
          (error.response.data && (error.response.data.message || error.response.data.error))) ||
        ERROR_MESSAGES.SAVE_RASCUNHO;
      toast.error(message);
      return false;
    } finally {
      formComposable.isSavingRascunho.value = false;
    }
  };

  const publicarCotacao = async () => {
    if (!props.idCotacao) {
      toast.error('ID da cotação não informado.');
      return false;
    }

    if (formComposable.dadosGerais.titulo) {
      const tituloTrimmed = formComposable.dadosGerais.titulo.trim();
      if (tituloTrimmed === '') {
        formComposable.dadosGerais.titulo = '';
        validationComposable.validate.titulo = 'O título não pode conter apenas espaços em branco';
        toast.error('O título não pode conter apenas espaços em branco');
        return false;
      }
      formComposable.dadosGerais.titulo = tituloTrimmed;
    }

    // Validar formulário
    const validation = validationComposable.validatePublicacao();
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return false;
    }

    // Montar payload
    const payload = payloadComposable.montarPayload({ forcePublicar: true });
    if (!payload.config) {
      toast.error(ERROR_MESSAGES.CONFIG_REQUIRED);
      return false;
    }

    formComposable.isPublicando.value = true;
    try {
      await api.put(
        `${API_ENDPOINTS.PUBLICAR_COTACAO}/${props.idCotacao}`,
        payload
      );
      toast.success(SUCCESS_MESSAGES.PUBLICAR_COTACAO);

      await formComposable.getCotacao();
      await store.refreshCotacao(props.idCotacao);
      await store.refresh();

      if (props.handleClose) {
        props.handleClose();
      }
      if (router.currentRoute.path !== '/cotacoes/listar-cotacoes') {
        await router.push('/cotacoes/listar-cotacoes');
      }

      return true;
    } catch (error) {
      const message =
        (error &&
          error.response &&
          (error.response.data && (error.response.data.message || error.response.data.error))) ||
        ERROR_MESSAGES.PUBLICAR_COTACAO;
      toast.error(message);
      return false;
    } finally {
      formComposable.isPublicando.value = false;
    }
  };

  const descartarRascunho = async () => {
    if (!props.idCotacao) {
      toast.error('Cotacao não encontrada');
      return;
    }

    isDeleting.value = true;

    showConfirm({
      title: 'Descartar rascunho',
      message: 'Tem certeza que deseja descartar o rascunho e remover os dados da cotação?',
      button: {
        yes: 'Confirmar',
        no: 'Cancelar'
      },
      callback: async (confirmed) => {
        if (confirmed) {
          try {
            await api.delete(`${API_ENDPOINTS.DELETAR_COTACAO}/${props.idCotacao}`);
            toast.success(SUCCESS_MESSAGES.DELETE_COTACAO);
            closeConfirmDeleteDialog();

            // Atualizar store
            store.removeCotacaoLocal(props.idCotacao);
            await store.refresh();

            if (props.handleClose) {
              props.handleClose();
            }
          } catch (error) {
            const message = (error && error.response.data.message) || ERROR_MESSAGES.DELETE_COTACAO;
            toast.error(message);
          } finally {
            isDeleting.value = false;
          }
        }
      }
    });
  };

  // Retornar tudo do form composable + métodos de ação
  return {
    // Do formComposable
    ...formComposable,
    setFornecedores: formComposable.setFornecedores,

    // Do validationComposable
    validate: validationComposable.validate,
    validatePeriodoForm: validationComposable.validatePeriodoForm,

    // Métodos de ação
    close,
    salvarRascunho,
    publicarCotacao,
    descartarRascunho,

    // Helper functions
    getStatusColor,
    getStatusIcon,
    getStatusName,

    // Estado adicional
    isDeleting,
    confirmDeleteDialog,
    closeConfirmDeleteDialog,
    hasFornecedoresSemContato,
  };
}
